import { useState, useEffect } from "react";
import { API } from "aws-amplify";
import {
  Box,
  Tabs,
  Tab,
  Text,
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
} from "grommet";
import { GetABMDIExam, getABMDIExamByStatus } from "../../graphql/queries";
import { UpdateABMDIExam } from "../../graphql/mutations";

export default function ABMDI({ sessionId, onClose }) {
  const [matchedSession, setMatchedSession] = useState(null);
  const [queuedSessions, setQueuedSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQueuedSessions = async () => {
      try {
        const response = await API.graphql({
          query: getABMDIExamByStatus,
          variables: {
            status: "QUEUE",
            sortDirection: "DESC",
            limit: 300,
            nextToken: null,
          },
        });
        if (response.data.abmdiExamByStatus.items.length > 0) {
          setQueuedSessions(response.data.abmdiExamByStatus.items);
        }
      } catch (error) {
        console.error("Error fetching queued sessions:", error);
      } finally {
        setLoading(false);
      }
    };

    const getABMDIExam = async () => {
      if (sessionId) {
        try {
          const response = await API.graphql({
            query: GetABMDIExam,
            variables: {
              id: sessionId,
            },
          });
          if (response.data.getABMDIExam != null) {
            setMatchedSession(response.data.getABMDIExam);
          }
        } catch (error) {
          console.error("Error fetching matched session:", error);
        }
      }
    };

    fetchQueuedSessions();
    getABMDIExam();
  }, [sessionId]);

  return (
    <Box width="large" pad="medium" overflow="auto">
      {onClose && (
        <>
          <Box
            direction="row"
            justify="between"
            align="center"
            margin={{ bottom: "small" }}
          >
            <Button
              icon={<span style={{ fontSize: "20px" }}>×</span>}
              onClick={onClose}
              plain
              hoverIndicator
              title="Close"
            />
            <Text size="large" weight="bold" textAlign="center">
              ABMDI Exam Links
            </Text>
            <Box width="40px" /> {/* Spacer to center the title */}
          </Box>
          <Text size="small" textAlign="center" color="black">
            Click outside of the sidebar to close
          </Text>
        </>
      )}
      {!onClose && (
        <Text
          size="large"
          weight="bold"
          textAlign="center"
          margin={{ bottom: "medium" }}
        >
          ABMDI Exam Links
        </Text>
      )}
      {sessionId ? (
        <Tabs>
          <Tab title="Matched Session">
            {matchedSession && (
              <Box>
                <Text size="small" textAlign="center" color="black">
                  Matched Session
                </Text>
                <SessionCard session={matchedSession} />
              </Box>
            )}
          </Tab>
          <Tab title="Queued Sessions">
            <Box>
              <Text size="small" textAlign="center" color="black">
                Queued Sessions
              </Text>
              {queuedSessions.length > 0 &&
                queuedSessions.map((session) => (
                  <SessionCard session={session} key={session.id} />
                ))}
            </Box>
          </Tab>
        </Tabs>
      ) : (
        <Box overflow="auto" fill={true}>
          <Text size="small" textAlign="center" color="black">
            Queued Sessions
          </Text>
          {loading ? (
            <Box align="center" pad="large">
              <Spinner size="medium" />
              <Text margin={{ top: "small" }}>Loading sessions...</Text>
            </Box>
          ) : (
            queuedSessions.length > 0 &&
            queuedSessions.map((session) => (
              <SessionCard session={session} key={session.id} />
            ))
          )}
        </Box>
      )}
    </Box>
  );
}

function SessionCard({ session }) {
  const [copiedButton, setCopiedButton] = useState(null);
  const [examFinished, setExamFinished] = useState(false);

  const handleCopy = async (text, buttonType) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedButton(buttonType);
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopiedButton(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  async function finishExam() {
    try {
      const response = await API.graphql({
        query: UpdateABMDIExam,
        variables: {
          input: {
            id: session.id,
            status: "FINISHED",
          },
        },
      });
      if (response.data.updateABMDIExam != null) {
        console.log(response.data.updateABMDIExam);
        setExamFinished(true);
        // Reset the finished state after 3 seconds
        setTimeout(() => setExamFinished(false), 3000);
      }
    } catch (err) {
      console.error("Failed to finish exam: ", err);
    }
  }
  return (
    <Card pad="small" height={{ min: "small" }} overflow="auto">
      <CardHeader align="center" justify="center">
        <Text size="large" weight="bold" textAlign="center">
          {session.candidate_name}
        </Text>
        <Text
          size="small"
          textAlign="center"
          color={session.status === "FINISHED" ? "status-ok" : "black"}
        >
          {session.status}
        </Text>
      </CardHeader>
      <CardBody pad={{ horizontal: "xlarge" }} gap="small">
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Email: </Text>
          <Text>{session.candidate_email}</Text>
        </Box>
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Exam Name: </Text>
          <Text>{session.exam_name}</Text>
        </Box>
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Tester Link: </Text>
          <Box direction="row" gap="medium" align="center">
            <Button
              label={copiedButton === "tester" ? "Copied!" : "Copy Tester Link"}
              onClick={() =>
                handleCopy(
                  "https://scorpion.caveon.com/take?launch_token=" +
                    session.launch_token,
                  "tester"
                )
              }
              primary
              color={copiedButton === "tester" ? "status-ok" : "black"}
            />
          </Box>
        </Box>
        <Box direction="row" gap="small" justify="between">
          <Text weight="bold">Proctor Link: </Text>
          <Box direction="row" gap="small" align="center">
            <Button
              label={
                copiedButton === "proctor" ? "Copied!" : "Copy Proctor Link"
              }
              onClick={() =>
                handleCopy(
                  "https://scorpion.caveon.com/proctor/" +
                    session.proctor_token,
                  "proctor"
                )
              }
              primary
              color={copiedButton === "proctor" ? "status-ok" : "black"}
            />
          </Box>
        </Box>
        <Box direction="row" gap="small" justify="between">
          <Button
            label={examFinished ? "Exam Finished!" : "Finish Exam"}
            onClick={finishExam}
            primary
            color={examFinished ? "status-ok" : "black"}
          />
        </Box>
      </CardBody>
    </Card>
  );
}
